import { useState, useEffect, useCallback, useMemo } from "react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "../../contexts/ToastContext";
import { useConfirm } from "../../contexts/ConfirmContext";
import {
  Settings,
  Edit2,
  Trash2,
  GripVertical,
  Plus,
  RotateCcw,
  X,
  Clock,
  Check,
  CheckCircle,
  LogIn,
  UserCheck,
  XCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";

// Estado de reserva tal y como lo devuelve el backend
// (prisma model ReservationStatus, tabla reservation_statuses)
interface ReservationStatus {
  id: string;
  code: string;
  name: string;
  color: string;
  bgColor: string;
  textColor: string;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
  isDefault: boolean;
  isFinal: boolean;
}

const ICON_MAP: Record<string, React.ElementType> = {
  Clock,
  Check,
  CheckCircle,
  LogIn,
  UserCheck,
  X,
  XCircle,
  AlertCircle,
};

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as { response?: { data?: { error?: string; message?: string } } }
    ).response;
    return response?.data?.error || response?.data?.message || fallback;
  }
  return fallback;
}

export default function ReservationStatuses() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();

  const [statuses, setStatuses] = useState<ReservationStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState<ReservationStatus | null>(
    null,
  );
  const [modalSaving, setModalSaving] = useState(false);

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const loadStatuses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get("/admin/reservation-statuses");
      const data: ReservationStatus[] = res.data.data ?? [];
      setStatuses([...data].sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err) {
      setError(
        extractErrorMessage(
          err,
          "No se pudieron cargar los estados de reserva",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const sortedStatuses = useMemo(
    () => [...statuses].sort((a, b) => a.sortOrder - b.sortOrder),
    [statuses],
  );

  // Activar / desactivar (optimista, revierte en caso de error)
  const toggleStatus = async (status: ReservationStatus) => {
    const nextActive = !status.isActive;
    setTogglingId(status.id);
    setStatuses((prev) =>
      prev.map((s) => (s.id === status.id ? { ...s, isActive: nextActive } : s)),
    );
    try {
      await apiClient.put(`/admin/reservation-statuses/${status.id}`, {
        isActive: nextActive,
      });
    } catch (err) {
      setStatuses((prev) =>
        prev.map((s) =>
          s.id === status.id ? { ...s, isActive: status.isActive } : s,
        ),
      );
      showToast(
        extractErrorMessage(err, "No se pudo actualizar el estado"),
        "error",
      );
    } finally {
      setTogglingId(null);
    }
  };

  // Borrar
  const handleDelete = async (status: ReservationStatus) => {
    const ok = await confirm({
      title: `¿Eliminar el estado "${status.name}"?`,
      message:
        "Esta acción no se puede deshacer. El estado dejará de estar disponible para nuevas reservas.",
      confirmText: "Sí, eliminar",
      danger: true,
    });
    if (!ok) return;

    setDeletingId(status.id);
    try {
      await apiClient.delete(`/admin/reservation-statuses/${status.id}`);
      setStatuses((prev) => prev.filter((s) => s.id !== status.id));
      showToast("Estado eliminado correctamente", "success");
    } catch (err) {
      showToast(
        extractErrorMessage(err, "No se pudo eliminar el estado"),
        "error",
      );
    } finally {
      setDeletingId(null);
    }
  };

  // Crear / editar (el modal envía color=textColor=hex + bgColor=hex)
  const handleSaveStatus = async (
    payload: StatusFormPayload,
    id: string | null,
  ) => {
    setModalSaving(true);
    const body = {
      code: payload.code,
      name: payload.name,
      color: payload.color, // compat: color = textColor
      textColor: payload.color,
      bgColor: payload.bgColor,
      icon: payload.icon,
      isDefault: payload.isDefault,
      isFinal: payload.isFinal,
    };
    try {
      if (id) {
        await apiClient.put(`/admin/reservation-statuses/${id}`, body);
        showToast("Estado actualizado correctamente", "success");
      } else {
        const nextSortOrder =
          statuses.length > 0
            ? Math.max(...statuses.map((s) => s.sortOrder)) + 1
            : 1;
        await apiClient.post("/admin/reservation-statuses", {
          ...body,
          sortOrder: nextSortOrder,
        });
        showToast("Estado creado correctamente", "success");
      }
      setShowModal(false);
      setEditingStatus(null);
      await loadStatuses();
    } catch (err) {
      showToast(extractErrorMessage(err, "No se pudo guardar el estado"), "error");
    } finally {
      setModalSaving(false);
    }
  };

  // Reordenar (drag & drop) — persiste sortOrder de los estados afectados
  const handleReorder = async (dragId: string, targetId: string) => {
    if (dragId === targetId) return;
    const sorted = [...statuses].sort((a, b) => a.sortOrder - b.sortOrder);
    const dragIndex = sorted.findIndex((s) => s.id === dragId);
    const targetIndex = sorted.findIndex((s) => s.id === targetId);
    if (dragIndex === -1 || targetIndex === -1) return;

    const reordered = [...sorted];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const originalOrderById = new Map(sorted.map((s) => [s.id, s.sortOrder]));
    const withNewOrder = reordered.map((s, idx) => ({
      ...s,
      sortOrder: idx + 1,
    }));
    const changed = withNewOrder.filter(
      (s) => originalOrderById.get(s.id) !== s.sortOrder,
    );

    setStatuses(withNewOrder);
    if (changed.length === 0) return;

    setReordering(true);
    try {
      await Promise.all(
        changed.map((s) =>
          apiClient.put(`/admin/reservation-statuses/${s.id}`, {
            sortOrder: s.sortOrder,
          }),
        ),
      );
    } catch (err) {
      showToast(
        extractErrorMessage(err, "No se pudo guardar el nuevo orden"),
        "error",
      );
      await loadStatuses();
    } finally {
      setReordering(false);
    }
  };

  // Restaurar predeterminados: crea los estados del sistema que falten
  // (no sobrescribe los existentes, ver POST /admin/reservation-statuses/seed)
  const handleSeedDefaults = async () => {
    const ok = await confirm({
      title: "Restaurar estados predeterminados",
      message:
        "Se crearán los estados del sistema que falten (pendiente, confirmada, llegada, sentada, completada, cancelada, no show...). Los estados existentes no se modificarán.",
      confirmText: "Restaurar",
    });
    if (!ok) return;

    setSeeding(true);
    try {
      const res = await apiClient.post("/admin/reservation-statuses/seed");
      const createdCount: number = res.data.data?.length ?? 0;
      showToast(
        createdCount > 0
          ? `Se ${createdCount === 1 ? "creó" : "crearon"} ${createdCount} estado${
              createdCount === 1 ? "" : "s"
            } predeterminado${createdCount === 1 ? "" : "s"}`
          : "Los estados predeterminados ya existían",
        "success",
      );
      await loadStatuses();
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          "No se pudieron restaurar los estados predeterminados",
        ),
        "error",
      );
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-primary-600" />
            Estados de Reserva
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Configura los estados disponibles para las reservas
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSeedDefaults}
            disabled={seeding}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {seeding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4" />
            )}
            Restaurar predeterminados
          </button>
          <button
            onClick={() => {
              setEditingStatus(null);
              setShowModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuevo estado
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900">
              Información importante
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              Los estados de reserva definen el flujo de trabajo de tu
              restaurante. Puedes activar o desactivar estados según tus
              necesidades. Los estados desactivados no aparecerán en el
              selector de la reserva. Arrastra las filas para reordenarlas.
            </p>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-700 truncate">{error}</p>
          </div>
          <button
            onClick={loadStatuses}
            className="text-sm font-medium text-red-700 hover:text-red-900 shrink-0"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Statuses Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">
              Estados configurados
            </h2>
          </div>

          {sortedStatuses.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">
              No hay estados configurados todavía.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {sortedStatuses.map((status) => {
                const IconComponent = ICON_MAP[status.icon || ""] || Clock;
                const isBusy =
                  togglingId === status.id || deletingId === status.id;
                return (
                  <div
                    key={status.id}
                    draggable={!reordering}
                    onDragStart={() => setDraggedId(status.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (draggedId) handleReorder(draggedId, status.id);
                      setDraggedId(null);
                    }}
                    onDragEnd={() => setDraggedId(null)}
                    className={`flex items-center flex-wrap sm:flex-nowrap gap-3 md:gap-4 p-4 transition-colors ${
                      !status.isActive ? "opacity-50 bg-gray-50" : ""
                    } ${draggedId === status.id ? "opacity-30" : ""}`}
                  >
                    {/* Drag Handle */}
                    <div className="hidden sm:flex cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 shrink-0">
                      <GripVertical className="w-5 h-5" />
                    </div>

                    {/* Color & Icon Preview */}
                    <div
                      className="w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: status.bgColor }}
                    >
                      <IconComponent
                        className="w-5 h-5 md:w-6 md:h-6"
                        style={{ color: status.color }}
                      />
                    </div>

                    {/* Status Info */}
                    <div className="flex-1 min-w-[140px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-medium text-gray-900">
                          {status.name}
                        </h3>
                        <span
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{
                            backgroundColor: status.bgColor,
                            color: status.color,
                          }}
                        >
                          {status.code}
                        </span>
                        {status.isDefault && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
                            Predeterminado
                          </span>
                        )}
                        {status.isFinal && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                            Final
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Color Preview */}
                    <div className="hidden md:flex items-center gap-2 shrink-0">
                      <div
                        className="w-6 h-6 rounded border border-gray-300"
                        style={{ backgroundColor: status.color }}
                        title="Color del texto"
                      />
                      <div
                        className="w-6 h-6 rounded border border-gray-300"
                        style={{ backgroundColor: status.bgColor }}
                        title="Color de fondo"
                      />
                    </div>

                    {/* Active Toggle */}
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={status.isActive}
                        disabled={isBusy}
                        onChange={() => toggleStatus(status)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600 peer-disabled:opacity-50"></div>
                    </label>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => {
                          setEditingStatus(status);
                          setShowModal(true);
                        }}
                        disabled={isBusy}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => handleDelete(status)}
                        disabled={isBusy}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Eliminar"
                      >
                        {deletingId === status.id ? (
                          <Loader2 className="w-4 h-4 text-red-500 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4 text-red-500" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Flow Preview */}
      {!loading && sortedStatuses.length > 0 && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="font-semibold text-gray-900 mb-4">
            Vista previa del flujo
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            {sortedStatuses
              .filter((s) => s.isActive)
              .map((status, index, arr) => {
                const IconComponent = ICON_MAP[status.icon || ""] || Clock;
                return (
                  <div key={status.id} className="flex items-center gap-2">
                    <div
                      className="flex items-center gap-2 px-3 py-2 rounded-lg"
                      style={{ backgroundColor: status.bgColor }}
                    >
                      <IconComponent
                        className="w-4 h-4"
                        style={{ color: status.color }}
                      />
                      <span
                        className="text-sm font-medium"
                        style={{ color: status.color }}
                      >
                        {status.name}
                      </span>
                    </div>
                    {index < arr.length - 1 && (
                      <span className="text-gray-400">→</span>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <StatusFormModal
          status={editingStatus}
          saving={modalSaving}
          onClose={() => {
            setShowModal(false);
            setEditingStatus(null);
          }}
          onSave={handleSaveStatus}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit Modal
// ---------------------------------------------------------------------------

interface StatusFormPayload {
  code: string;
  name: string;
  color: string; // hex — etiqueta UI "Color del texto", se envía como textColor (+ color de compat)
  bgColor: string; // hex — etiqueta UI "Color de fondo"
  icon: string;
  isDefault: boolean;
  isFinal: boolean;
}

interface StatusFormModalProps {
  status: ReservationStatus | null; // null = crear
  saving: boolean;
  onClose: () => void;
  onSave: (payload: StatusFormPayload, id: string | null) => void;
}

function StatusFormModal({
  status,
  saving,
  onClose,
  onSave,
}: StatusFormModalProps) {
  const isEditing = status !== null;
  const [formData, setFormData] = useState<StatusFormPayload>(() =>
    status
      ? {
          code: status.code,
          name: status.name,
          // Al editar, los inputs de color se rellenan desde el registro de la BD
          color: status.textColor || status.color,
          bgColor: status.bgColor,
          icon: status.icon || "Clock",
          isDefault: status.isDefault,
          isFinal: status.isFinal,
        }
      : {
          code: "",
          name: "",
          color: "#374151",
          bgColor: "#F3F4F6",
          icon: "Clock",
          isDefault: false,
          isFinal: false,
        },
  );
  const [formError, setFormError] = useState<string | null>(null);

  const handleCodeChange = (value: string) => {
    const sanitized = value
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z_]/g, "");
    setFormData((prev) => ({ ...prev, code: sanitized }));
  };

  const handleSubmit = () => {
    if (!isEditing && !/^[a-z_]+$/.test(formData.code)) {
      setFormError(
        "El código es obligatorio y solo puede contener letras minúsculas y guiones bajos (ej: en_camino)",
      );
      return;
    }
    if (!formData.name.trim()) {
      setFormError("El nombre es obligatorio");
      return;
    }
    setFormError(null);
    onSave(formData, status?.id ?? null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90dvh] overflow-y-auto">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditing ? "Editar Estado" : "Nuevo Estado"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Preview */}
          <div className="flex justify-center">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-lg"
              style={{ backgroundColor: formData.bgColor }}
            >
              {(() => {
                const IconComponent = ICON_MAP[formData.icon] || Clock;
                return (
                  <IconComponent
                    className="w-5 h-5"
                    style={{ color: formData.color }}
                  />
                );
              })()}
              <span className="font-medium" style={{ color: formData.color }}>
                {formData.name || "Nombre del estado"}
              </span>
            </div>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {formError}
            </div>
          )}

          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código
            </label>
            <input
              type="text"
              value={formData.code}
              disabled={isEditing}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="ej: en_camino"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:text-gray-500 font-mono text-sm"
            />
            <p className="text-xs text-gray-400 mt-1">
              {isEditing
                ? "El código no se puede modificar."
                : "Solo minúsculas y guiones bajos, sin espacios ni acentos."}
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del estado
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Colors (HEX) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color del texto
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className="w-12 h-10 shrink-0 p-0.5 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData({ ...formData, color: e.target.value })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Color de fondo
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={formData.bgColor}
                  onChange={(e) =>
                    setFormData({ ...formData, bgColor: e.target.value })
                  }
                  className="w-12 h-10 shrink-0 p-0.5 rounded border border-gray-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.bgColor}
                  onChange={(e) =>
                    setFormData({ ...formData, bgColor: e.target.value })
                  }
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                />
              </div>
            </div>
          </div>

          {/* Icon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icono
            </label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(ICON_MAP).map(([key, IconComponent]) => (
                <button
                  key={key}
                  onClick={() => setFormData({ ...formData, icon: key })}
                  className={`p-3 rounded-lg border transition-colors ${
                    formData.icon === key
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <IconComponent className="w-5 h-5 mx-auto text-gray-600" />
                </button>
              ))}
            </div>
          </div>

          {/* Flags */}
          <div className="space-y-2 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isDefault}
                onChange={(e) =>
                  setFormData({ ...formData, isDefault: e.target.checked })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Estado predeterminado para nuevas reservas
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isFinal}
                onChange={(e) =>
                  setFormData({ ...formData, isFinal: e.target.checked })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Estado final (la reserva se considera finalizada)
              </span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 p-5 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
